import { Navigate, Outlet, useNavigate, useSearch } from '@tanstack/react-router'
import { Loader2 } from 'lucide-react'
import { useEffect, Suspense } from 'react'

import { LoginPage } from '@/features/auth/LoginPage'
import { Sidebar } from '@/ui/layout/Sidebar'
import { Header } from '@/ui/layout/Header'
import { useAuthStore } from '@/core/store/authStore'
import { useDashboardStore } from '@/core/store/dashboardStore'
import type { UserRole } from '@/core/store/authStore'
import { useSessionsSync } from '@/features/classes/hooks'
import { cn } from '@/core/lib/utils'

// ─── Auth-gate layout (pathless) ─────────────────────────────────────────────
// Wraps everything that requires (or reacts to) authentication.
// This is a React component so it re-renders reactively when the Zustand auth
// state changes — unlike beforeLoad which only runs once at route resolution.

/** Internal SPA paths only: blocks external redirects via //host or scheme://. */
// eslint-disable-next-line react-refresh/only-export-components -- exported for tests
export function isSafeInternalPath(path: string): boolean {
  return path.startsWith('/') && !path.startsWith('//') && !path.includes('://')
}

export function AuthGateLayout() {
  const isCheckingAuth = useAuthStore((s) => s.isCheckingAuth)
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)

  const navigate = useNavigate()
  const search = useSearch({ strict: false }) as Record<string, unknown>

  useEffect(() => {
    if (isCheckingAuth || !isAuthenticated) {
      return
    }
    const redirect = typeof search.redirect === 'string' ? search.redirect : ''
    if (redirect && isSafeInternalPath(redirect)) {
      void navigate({ to: redirect })
    }
  }, [isCheckingAuth, isAuthenticated, search, navigate])

  if (isCheckingAuth) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center gap-4">
        <div className="flex flex-col items-center gap-3">
          <img
            src="/images/logo/ub-logo-128.webp"
            alt="UBSafety"
            className="w-16 h-16 object-contain"
          />
          <span className="text-lg font-bold text-navy">UBSafety</span>
        </div>
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    )
  }

  if (!isAuthenticated) {
    return <LoginPage />
  }

  return <Outlet />
}

/** Role guard wrapper — redirects if user does not have required role. */
export function RoleGuard({ allowedRoles, children }: { allowedRoles: UserRole[]; children: React.ReactNode }) {
  const user = useAuthStore((s) => s.user)

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center gap-4">
        <Loader2 className="w-9 h-9 text-primary animate-spin" />
        <p className="text-slate-500 text-sm">Loading...</p>
      </div>
    )
  }

  if (!allowedRoles.includes(user.role as UserRole)) {
    return <Navigate to="/overview" replace />
  }

  return <>{children}</>
}

export function PageLoader() {
  return (
    <div className="flex items-center justify-center py-24">
      <Loader2 className="w-8 h-8 animate-spin text-primary" />
    </div>
  )
}

export function DashboardLayout() {
  const sidebarOpen = useDashboardStore((s) => s.sidebarOpen)
  const setSidebarOpen = useDashboardStore((s) => s.setSidebarOpen)

  // Auto-close sidebar when resizing to mobile
  useEffect(() => {
    function handleResize() {
      if (window.innerWidth < 1024) {
        setSidebarOpen(false)
      } else {
        setSidebarOpen(true)
      }
    }
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [setSidebarOpen])

  // Hook owns the full sessions sync lifecycle:
  // Component → useSessionsSync (Hook) → API/Realtime → Zustand store (state)
  useSessionsSync()

  return (
    <div className="min-h-screen bg-gray-50">
      <Sidebar />
      <Header />
      <main
        className={cn(
          'transition-all duration-300 pt-16',
          sidebarOpen ? 'sidebar-open-ml' : 'sidebar-closed-ml'
        )}
      >
        <div className="p-4 md:p-6">
          <Suspense fallback={<PageLoader />}>
            <Outlet />
          </Suspense>
        </div>
      </main>
    </div>
  )
}

export function AdminLayout() {
  return (
    <RoleGuard allowedRoles={['admin']}>
      <Outlet />
    </RoleGuard>
  )
}
